import { Link, Heading, Box, Text, Divider, Icon } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { IconInfo } from 'assets/icons';
import Button from 'components/Button';
import { FormikInput } from 'components/Formik';
import { routePaths } from 'global/routePaths';
import useRequestPaylocityIntegration from 'hooks/auth/useRequestPaylocityIntegration';

import { initialPaylocityIntegrationFormData, paylocityIntegrationFormValidationSchema } from './schema';

interface IPaylocityIntegrationForm {
  paylocityCompanyId: string | null;
}

const PaylocityIntegrationForm: React.FC<IPaylocityIntegrationForm> = ({ paylocityCompanyId }) => {
  const navigate = useNavigate();

  const { t } = useTranslation(['common', 'auth']);

  const { isLoading, mutateAsync } = useRequestPaylocityIntegration();

  const handleFormSubmit = (values: typeof initialPaylocityIntegrationFormData) => {
    const postData = {
      paylocity_company_id: paylocityCompanyId,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      company_name: values.company_name,
      phone: values.phone,
    };

    mutateAsync(postData).then(() => {
      navigate(routePaths.auth.login);
    });
  };

  return (
    <Box
      bg="white"
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight="100vh"
      p={{
        base: '32px 16px',
        md: '60px',
        lg: '32px',
        xl: '60px',
        '2xl': '100px',
      }}
    >
      <Heading as="h2" size="3xl" mt={{ base: '8', md: '14' }} mb="9">
        Paylocity Integration Form
      </Heading>
      <Text fontSize="lg" borderRadius="lg" border="1px solid" padding="4" px="7" borderColor="gray.200">
        <Icon as={IconInfo} /> If you are already a Timeero user, Please
        <Link as={RouterLink} to={routePaths.auth.login} color="primary.200" ml="1">
          {t('auth:logIn')}
        </Link>
        &nbsp;and try integrating with Paylocity again.
      </Text>
      <Divider colorScheme="gray" borderStyle="dashed" mt="9" mb="6" />
      <Box maxWidth="500px" width="100%">
        <Formik
          enableReinitialize
          initialValues={initialPaylocityIntegrationFormData}
          validationSchema={paylocityIntegrationFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form noValidate>
              <FormikInput type="text" name="first_name" label="First Name" formControlProps={{ isRequired: true }} />
              <FormikInput type="text" name="last_name" label="Last Name" formControlProps={{ isRequired: true }} />
              <FormikInput
                type="text"
                name="company_name"
                label="Company Name"
                formControlProps={{ isRequired: true }}
              />
              <FormikInput type="text" name="email" label="Email" formControlProps={{ isRequired: true }} />
              <FormikInput type="text" name="phone" label="Phone Number" formControlProps={{ isRequired: true }} />

              <Button type="submit" colorScheme="secondary" width="100%" size="lg" isLoading={isLoading}>
                Request Integration
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default PaylocityIntegrationForm;
